import { Fragment, useId } from 'react';
import SectionCard from '../../components/SectionCard.jsx';
import { OPERATOR, matrixForDisplay } from '../../../../shared/permissions.mjs';
import { TENANT_ROLE_DESCRIPTIONS, TENANT_ROLE_LABELS } from '../../../../shared/solution.mjs';

// What each role can do (ENTRAID-358, spec 2026-11-03-permission-bitmask-table-design.md 5.6): the shared permission
// table the API enforces, rendered as it is, so this view and the API never differ. Resources grouped by section, one
// column per tenant role plus the IPC Global operator (shown to customers too, decision Q3), the actions of each cell
// and its conditions in plain words. Display only: the API decides every request.

/** The column header of the operator; the tenant roles use TENANT_ROLE_LABELS. */
export const OPERATOR_LABEL = 'IPC Global operator';

const OPERATOR_DESCRIPTION = 'IPC Global staff who run onGuard for every customer. Every operator permission is listed here; there is no hidden override.';

// The action names of shared/permissions.mjs ACTIONS in plain words.
const ACTION_WORDS = Object.freeze({
  read: 'View',
  create: 'Add',
  update: 'Change',
  delete: 'Remove',
  execute: 'Run',
  reveal: 'Reveal',
  export: 'Export',
  attest: 'Sign off',
});

const labelOf = (principal) => (principal === OPERATOR ? OPERATOR_LABEL : TENANT_ROLE_LABELS[principal] ?? principal);
const descriptionOf = (principal) => (principal === OPERATOR ? OPERATOR_DESCRIPTION : TENANT_ROLE_DESCRIPTIONS[principal] ?? '');
const capitalize = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

/**
 * The matrix laid out for reading: a condition held by every granting cell of a column (the auditor's live access, the
 * reviewer's registration) is said once under that column's header; every other condition becomes a numbered note.
 */
export function rolesMatrixView(facts) {
  const { principals, sections } = matrixForDisplay(facts);
  const columnNotes = {};
  for (const p of principals) {
    const granting = sections.flatMap((s) => s.rows.map((r) => r.cells[p])).filter((c) => c.actions.length > 0);
    columnNotes[p] = granting.length === 0 ? [] : granting[0].conditions.filter((w) => granting.every((c) => c.conditions.includes(w)));
  }
  const notes = [];
  const noteOf = (words) => {
    let i = notes.indexOf(words);
    if (i === -1) { notes.push(words); i = notes.length - 1; }
    return i + 1;
  };
  const view = sections.map((s) => ({
    label: s.label,
    rows: s.rows.map((r) => ({
      resource: r.resource,
      label: r.label,
      cells: principals.map((p) => ({
        principal: p,
        actions: r.cells[p].actions.map((a) => ACTION_WORDS[a] ?? a),
        notes: r.cells[p].conditions.filter((w) => !columnNotes[p].includes(w)).map(noteOf),
      })),
    })),
  }));
  return { principals, columnNotes, sections: view, notes };
}

/**
 * @param {{ facts?: object }} props - the facts for matrixForDisplay; `flags.reviewer` shows the reviewer column.
 */
export default function RolesMatrix({ facts }) {
  const { principals, columnNotes, sections, notes } = rolesMatrixView(facts);
  // Each column's role description: visible text below the table (readable on touch, never a tooltip only), and the
  // accessible description of its column header.
  const baseId = useId();
  const roleDescId = (p) => `${baseId}-role-${p}`;
  return (
    <SectionCard
      title="What each role can do"
      description="The same permission table onGuard checks on every request. A blank cell means no access."
      testId="roles-matrix"
    >
      <div role="region" tabIndex={0} aria-label="What each role can do, table" className="overflow-x-auto rounded-xl border border-ipc-border">
        <table className="min-w-full text-left text-[13px]" aria-label="What each role can do">
          <thead className="bg-ipc-surface text-ipc-ink">
            <tr>
              <th scope="col" className="sticky left-0 z-10 min-w-[9rem] bg-ipc-surface px-3 py-2 align-bottom font-semibold sm:min-w-[11rem]">Area</th>
              {principals.map((p) => (
                <th key={p} scope="col" className="min-w-[7.5rem] px-3 py-2 align-bottom font-semibold" data-principal={p} aria-describedby={roleDescId(p)}>
                  {labelOf(p)}
                  {columnNotes[p].map((w) => <span key={w} className="mt-0.5 block text-[12px] font-normal text-ipc-muted">{capitalize(w)}</span>)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sections.map((s) => (
              <Fragment key={s.label}>
                <tr className="border-t border-ipc-border bg-ipc-surface">
                  <th scope="rowgroup" colSpan={principals.length + 1} className="px-3 py-1.5 text-[12px] font-semibold text-ipc-ink">{s.label}</th>
                </tr>
                {s.rows.map((r) => (
                  <tr key={r.resource} className="border-t border-ipc-border align-top" data-resource={r.resource}>
                    <th scope="row" className="sticky left-0 bg-white px-3 py-2 font-normal text-ipc-ink">{r.label}</th>
                    {r.cells.map((c) => (
                      <td key={c.principal} className="px-3 py-2 text-ipc-ink" data-principal={c.principal}>
                        {c.actions.length > 0 ? (
                          <>
                            {c.actions.join(', ')}
                            {c.notes.length > 0 ? <sup className="ml-0.5 text-[12px] text-ipc-muted">{c.notes.join(',')}</sup> : null}
                          </>
                        ) : <span className="sr-only">No access</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <dl className="mt-3 grid gap-x-3 gap-y-1 text-[12px] sm:grid-cols-[auto_1fr]" aria-label="About the roles" data-testid="roles-matrix-legend">
        {principals.map((p) => (
          <Fragment key={p}>
            <dt className="font-semibold text-ipc-ink">{labelOf(p)}</dt>
            <dd id={roleDescId(p)} className="text-ipc-muted">{descriptionOf(p)}</dd>
          </Fragment>
        ))}
      </dl>
      {notes.length > 0 ? (
        <ol className="mt-3 space-y-0.5 text-[12px] text-ipc-muted" aria-label="Notes">
          {notes.map((w, i) => <li key={w}><sup>{i + 1}</sup> {capitalize(w)}.</li>)}
        </ol>
      ) : null}
    </SectionCard>
  );
}
